<template>
  <div>
    <div class="section">
      <div class="container">
        <!-- Page Header -->
        <div class="mb-lg">
          <div class="columns is-vcentered">
            <div class="column is-one-third">
              <breadcrumbs :history="[{ label: 'Connections', name: 'connections'}]" current="Invitations" />
            </div>
            <div class="column is-one-third">
              <h1 class="has-text-centered is-size-4">Invitations</h1>
            </div>
          </div>
        </div>
        <!-- Filters -->
        <div class="has-background-white rounded-lg pa-md mb-xxs">
          <div class="columns">
            <div class="column">
              <InvitationFilters @filter="filter" :applied-filters="filters" />
            </div>
            <div class="column is-narrow">
              <b-select v-model="orderBy" expanded @input="sort" size="is-medium">
                <option value="id">Oldest</option>
                <option value="-id">Most Recent</option>
              </b-select>
            </div>
            <div v-if="app.environment !== 'production'" class="column is-narrow">
              <b-button
                size="is-medium"
                @click.prevent="showInvitationModal = true"
                outlined>
                Invite Supplier
              </b-button>
            </div>
          </div>
        </div>
        <!-- Content -->
        <div>
          <div>
            <b-table
              :data="results"
              :mobile-cards="false"
              :loading="loading"
              hoverable
              class="rounded-lg is-size-7">
              <template>
                <!-- ID -->
                <b-table-column
                  label="ID"
                  cell-class="nowrap has-text-grey-light"
                  width="64"
                  v-slot="props">
                  {{ props.row.id }}
                </b-table-column>
                <!-- Company -->
                <b-table-column
                  label="Company"
                  cell-class="nowrap"
                  width="200"
                  v-slot="props">
                  {{ props.row.company_name }}
                </b-table-column>
                <!-- Contact -->
                <b-table-column
                  label="Contact"
                  cell-class="nowrap"
                  v-slot="props">
                  <p>{{ props.row.first_name }} {{ props.row.last_name }}</p>
                  <p class="has-text-grey-light">{{ props.row.email }}</p>
                </b-table-column>
                <!-- Sent -->
                <b-table-column
                  label="Created"
                  cell-class="nowrap"
                  width="128"
                  centered
                  v-slot="props">
                  {{ $dates.utc(props.row.created_at).local().format('MMM D, YYYY') }}
                </b-table-column>
                <!-- Status -->
                <b-table-column
                  label="Status"
                  cell-class="nowrap"
                  width="128"
                  centered
                  v-slot="props">
                  <p class="has-text-weight-bold">
                    <span v-if="props.row.status === 'accepted'" class="has-text-success">{{ $utils.upperFirst(props.row.status) }}</span>
                    <span v-else class="has-text-grey">{{ $utils.upperFirst(props.row.status) }}</span>
                  </p>
                </b-table-column>
                <!-- Actions -->
                <b-table-column
                  label=""
                  cell-class="nowrap"
                  width="128"
                  centered
                  v-slot="props">
                  <div v-if="props.row.status === 'pending'">
                    <b-button
                      size="is-small"
                      class="mr-sm"
                      icon-right="pencil"
                      @click.prevent="onOpenEdit(props.row)"
                      outlined />
                    <b-button
                      v-if="props.row.sendgrid_events.length"
                      size="is-small"
                      style="min-width: 80px"
                      @click.prevent="onOpenDetails(props.row)"
                      outlined>
                      More Info
                    </b-button>
                    <ResendInvitation v-else :invitation="props.row" size="is-small" style="min-width: 80px" />
                  </div>
                </b-table-column>
              </template>
              <template #empty>
                <div class="py-xxl has-text-grey has-text-centered">
                  {{ (loading) ? "Loading data..." : 'No invitations found!' }}
                </div>
              </template>
            </b-table>
          </div>
          <div class="mt-md">
            <b-pagination
              :total="total"
              :current.sync="currentPage"
              :rounded="true"
              :per-page="perPage"
              @change="paginate">
            </b-pagination>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal: Invitation -->
    <b-modal
      has-modal-card
      scroll="keep"
      :active.sync="showInvitationModal"
      :can-cancel="['x']"
      ><InvitationModal @saved="load"/>
    </b-modal>
    <!-- Modal: More Info -->
    <b-modal
      has-modal-card
      scroll="keep"
      :active.sync="showMoreInfoModal"
      :can-cancel="['x']"
      @close="onClose"
      ><PendingInvitationDetails :invitation="selectedRow"/>
    </b-modal>
    <!-- Modal: Edit Invitation -->
    <b-modal
      has-modal-card
      scroll="keep"
      :active.sync="showEditModal"
      :can-cancel="['x']"
      @close="onClose"
      ><EditInvitationModal v-if="selectedRow" :invitation="selectedRow" @saved="load"/>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filterable from '@/mixins/Filterable'
import ResendInvitation from '@/components/Models/Connection/ResendInvitation'
import InvitationFilters from '../components/List/InvitationFilters'
import InvitationModal from '@/components/Models/Connection/InvitationModal'
import EditInvitationModal from '@/components/Models/Connection/EditInvitationModal'
import PendingInvitationDetails from '@/components/Models/Connection/PendingInvitationDetails'

export default {
  components: {
    InvitationFilters,
    InvitationModal,
    EditInvitationModal,
    ResendInvitation,
    PendingInvitationDetails
  },
  data () {
    return {
      key: 'invitations',
      orderBy: '-id',
      showInvitationModal: false,
      showMoreInfoModal: false,
      showEditModal: false,
      selectedRow: null
    }
  },
  computed: {
    ...mapGetters(['app', 'context']),
    endpoint () {
      return this.context.route + '/connection-invites/'
    }
  },
  methods: {
    onOpenDetails (row) {
      this.selectedRow = row
      this.showMoreInfoModal = true
    },
    onOpenEdit (row) {
      this.selectedRow = row
      this.showEditModal = true
    },
    onClose () {
      this.showMoreInfoModal = false
      this.showEditModal = false
      this.selectedRow = null
    }
  },
  mixins: [Filterable],
  mounted () {
    this.load()
  }
}
</script>
